import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { graphql, useStaticQuery } from "gatsby";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuthApi } from "./useAuthApi";
import * as Either from "features/common/either";
import { AuthErrorBoxQuery } from "types/graphql-types";
import AuthErrorModal from "./AuthErrorModal";
import * as AuthState from "./auth-state";
import { userDetailsToDataLayer } from "src/utils/analytics/tracking";
import { CONSUMER_ID_KEY, User } from "./user";
import { removeWebStorageItem, setWebStorageItem } from "src/utils/web-storage";
import { useWebStorageAvailability } from "src/utils/web-storage/useWebStorageAvailability";
import { match, P } from "ts-pattern";
import { readLoginCallbackParam } from "./manual-login";

interface AuthContextType {
  state: AuthState.AuthState;
  login: () => void;
  logout: () => void;
  refreshAuthStatus: () => Promise<void>;
  clearError: () => void;
  justLoggedInManually: boolean;
  acknowledgeManualLogin: () => void;
}

const redirectPath = () => window.location.pathname + window.location.search;

type DeriveAuthStateParams = {
  isLoading: boolean;
  loginError: boolean;
  logoutError: boolean;
  user: User | undefined;
};

export const deriveAuthState = ({
  isLoading,
  loginError,
  logoutError,
  user
}: DeriveAuthStateParams): AuthState.AuthState => {
  if (isLoading) return { status: "loading" };
  if (loginError) return { status: "error", error: "Failed to initiate login" };
  if (logoutError)
    return { status: "error", error: "Failed to initiate logout" };
  if (user) return { status: "authenticated", user };
  return { status: "anonymous" };
};

const isUnauthorized = (error: unknown): boolean =>
  error !== null &&
  typeof error === "object" &&
  "type" in error &&
  error.type === "HttpError" &&
  "statusCode" in error &&
  error.statusCode === 401;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const [silentUrl, setSilentUrl] = useState<string | null>(null);
  const [redirectInProgress, setRedirectInProgress] = useState(false);
  const [silentLoginSettled, setSilentLoginSettled] = useState(false);
  const [justLoggedInManually, setJustLoggedInManually] = useState(false);
  const manualLoginPending = useRef(false);
  const localStorageIsAvailable = useWebStorageAvailability("local");
  const silentLoginTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const refetchRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const authApi = useAuthApi();
  const data = useStaticQuery<AuthErrorBoxQuery>(graphql`
    query AuthErrorBox {
      fycPagePath
    }
  `);

  const authStatusQuery = useQuery({
    queryKey: ["auth-status"],
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const result = await authApi.status();
      if (Either.isLeft(result)) throw result.left;
      return result.right;
    }
  });

  const { refetch } = authStatusQuery;
  refetchRef.current = async () => {
    await refetch();
  };

  const loginMutation = useMutation({
    mutationFn: async () => {
      const result = await authApi.loginUrl(redirectPath());
      if (Either.isLeft(result)) throw result.left;
      return result.right;
    },
    onSuccess: (authUrl) => {
      if (!authUrl) return;
      setRedirectInProgress(true);
      window.location.href = authUrl;
    }
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      const result = await authApi.logoutUrl(redirectPath());
      if (Either.isLeft(result)) throw result.left;
      return result.right;
    },
    onSuccess: (authUrl) => {
      setRedirectInProgress(true);
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        window.location.reload();
      }
    }
  });

  const silentLoginMutation = useMutation({
    mutationFn: async () => {
      const result = await authApi.silentLoginUrl();
      if (Either.isLeft(result)) throw result.left;
      return result.right;
    },
    onSuccess: (authUrl) => {
      if (silentLoginTimeoutId.current)
        clearTimeout(silentLoginTimeoutId.current);
      setSilentUrl(authUrl);
      silentLoginTimeoutId.current = setTimeout(() => {
        setSilentUrl(null);
        silentLoginTimeoutId.current = null;
        setSilentLoginSettled(true);
      }, 5000);
    },
    onError: () => {
      setSilentLoginSettled(true);
    }
  });

  const awaitingSilentLogin =
    !silentLoginSettled && isUnauthorized(authStatusQuery.error);

  const isLoading =
    authStatusQuery.isLoading ||
    authStatusQuery.isFetching ||
    awaitingSilentLogin ||
    silentUrl !== null ||
    loginMutation.isPending ||
    logoutMutation.isPending ||
    redirectInProgress;

  const authState = deriveAuthState({
    isLoading,
    loginError: loginMutation.isError,
    logoutError: logoutMutation.isError,
    user: authStatusQuery.data?.user
  });

  const clearError = (): void => {
    loginMutation.reset();
    logoutMutation.reset();
  };

  useEffect(() => {
    userDetailsToDataLayer(authState);
  }, [authState.status]);

  const isLogoutMutationPending = logoutMutation.isPending;

  useEffect(() => {
    if (!localStorageIsAvailable) return;

    match(authState)
      .with({ status: "authenticated" }, ({ user }) => {
        setWebStorageItem("local", CONSUMER_ID_KEY, user.id);
      })
      .with({ status: "anonymous" }, () => {
        removeWebStorageItem("local", CONSUMER_ID_KEY);
      })
      .with({ status: "error" }, () => {
        removeWebStorageItem("local", CONSUMER_ID_KEY);
      })
      .with({ status: "loading" }, () => {
        if (isLogoutMutationPending) {
          removeWebStorageItem("local", CONSUMER_ID_KEY);
        }
      })
      .exhaustive();
  }, [authState.status, localStorageIsAvailable, isLogoutMutationPending]);

  useEffect(() => {
    const { isManualLogin, cleanedUrl } = readLoginCallbackParam(
      window.location
    );
    // Only ever raise the flag: a second run (StrictMode) sees the cleaned URL
    if (isManualLogin) manualLoginPending.current = true;
    if (cleanedUrl !== null) {
      window.history.replaceState(window.history.state, "", cleanedUrl);
    }
  }, []);

  useEffect(() => {
    match(authState.status)
      .with("authenticated", () => {
        if (!manualLoginPending.current) return;
        manualLoginPending.current = false;
        setJustLoggedInManually(true);
      })
      .with(P.union("anonymous", "error"), () => {
        manualLoginPending.current = false;
      })
      .with("loading", () => {})
      .exhaustive();
  }, [authState.status]);

  const silentLogin = silentLoginMutation.mutate;

  useEffect(() => {
    if (!awaitingSilentLogin) return;
    silentLogin();
  }, [awaitingSilentLogin, silentLogin]);

  useEffect(() => {
    return () => {
      if (silentLoginTimeoutId.current) {
        clearTimeout(silentLoginTimeoutId.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === "SILENT_LOGIN_RESULT" && event.data.login) {
        if (silentLoginTimeoutId.current) {
          clearTimeout(silentLoginTimeoutId.current);
          silentLoginTimeoutId.current = null;
        }
        setSilentUrl(null);

        if (event.data.login !== "success") {
          setSilentLoginSettled(true);
          return;
        }

        try {
          await refetchRef.current();
        } finally {
          setSilentLoginSettled(true);
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const contextValue: AuthContextType = {
    state: authState,
    login: loginMutation.mutate,
    logout: logoutMutation.mutate,
    refreshAuthStatus: async () => {
      await refetch();
    },
    clearError,
    justLoggedInManually,
    acknowledgeManualLogin: () => setJustLoggedInManually(false)
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {silentUrl && (
        <iframe
          title={"silent-login"}
          src={silentUrl}
          style={{ display: "none" }}
        />
      )}
      {AuthState.isError(authState) && (
        <AuthErrorModal onClose={clearError} fycUrl={data.fycPagePath ?? "/"} />
      )}
      {children}
    </AuthContext.Provider>
  );
};

const defaultAuthContext: AuthContextType = {
  state: { status: "anonymous" },
  login: () => {},
  logout: () => {},
  refreshAuthStatus: async () => {},
  clearError: () => {},
  justLoggedInManually: false,
  acknowledgeManualLogin: () => {}
};

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  const isBrowser = typeof window !== "undefined";
  if (context === undefined && isBrowser) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return useContext(AuthContext) ?? defaultAuthContext;
}
